/**
 * The bet log's filter model, kept out of the component so it is one pure
 * mapping from the URL to a `BetQuery` and back.
 *
 * Every filter on this card lives in the URL. The original back-office kept its
 * "Max Row" and status dropdown in page state, so the link an operator pasted
 * into a support thread reopened the unfiltered table and the two people were
 * looking at different rows while typing at each other.
 */

import type { BetQuery } from '@/data/api'
import type { BetSide, BetStatus, DateRange } from '@/data/types'

/* ------------------------------------------------------------- vocabulary -- */

export type BetStatusFilter =
  | 'open'
  | 'all'
  | 'matched'
  | 'unmatched'
  | 'settled'
  | 'void'
  | 'cancelled'

export type BetSideFilter = 'all' | BetSide

/**
 * `open` is the default because the panels above this table are the OPEN book,
 * and a log that opened on settled rows would look like it disagreed with them.
 * `all` is one click away and says so in the label.
 */
export const BET_STATUS_FILTERS: { id: BetStatusFilter; label: string }[] = [
  { id: 'open', label: 'All open (matched + unmatched)' },
  { id: 'all', label: 'All bets, including settled' },
  { id: 'matched', label: 'Matched' },
  { id: 'unmatched', label: 'Unmatched' },
  { id: 'settled', label: 'Settled' },
  { id: 'void', label: 'Void' },
  { id: 'cancelled', label: 'Cancelled' },
]

const STATUS_QUERY: Record<BetStatusFilter, BetStatus[] | undefined> = {
  open: ['matched', 'unmatched'],
  all: undefined,
  matched: ['matched'],
  unmatched: ['unmatched'],
  settled: ['settled'],
  void: ['void'],
  cancelled: ['cancelled'],
}

/** Whether this choice can put rows on screen that the book above excludes. */
export const admitsSettled = (status: BetStatusFilter): boolean =>
  status === 'all' || status === 'settled' || status === 'void' || status === 'cancelled'

export const BET_SIDE_FILTERS: { id: BetSideFilter; label: string }[] = [
  { id: 'all', label: 'Back and lay' },
  { id: 'back', label: 'Back' },
  { id: 'lay', label: 'Lay' },
]

/**
 * The video's "Max Row". Here it is a page SIZE rather than a cap, because a
 * cap silently truncates: the original showed 500 of 1,789 bets with nothing on
 * screen saying the other 1,289 existed.
 */
export const BET_ROW_CAPS = [50, 100, 250, 500] as const

export const DEFAULT_ROW_CAP = 100

/* ---------------------------------------------------------------- shape -- */

export interface BetFilters {
  /** Member username or display name. */
  q: string
  /** '' means every selection. */
  selection: string
  side: BetSideFilter
  status: BetStatusFilter
  /** isoDay, or '' for open-ended. */
  from: string
  to: string
  rows: number
  page: number
}

/** Every URL key this card owns. Clearing writes null to each of them. */
export const BET_FILTER_PARAMS = ['q', 'sel', 'side', 'status', 'from', 'to', 'rows', 'page'] as const

export const CLEARED_BET_FILTERS: Record<string, null> = Object.fromEntries(
  BET_FILTER_PARAMS.map((key) => [key, null]),
)

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

export function readBetFilters(params: URLSearchParams): BetFilters {
  const statusParam = params.get('status')
  const status = BET_STATUS_FILTERS.find((entry) => entry.id === statusParam)?.id ?? 'open'
  const sideParam = params.get('side')
  const side = BET_SIDE_FILTERS.find((entry) => entry.id === sideParam)?.id ?? 'all'
  const rowsParam = Number(params.get('rows'))
  const rows = (BET_ROW_CAPS as readonly number[]).includes(rowsParam) ? rowsParam : DEFAULT_ROW_CAP
  const pageParam = Number(params.get('page'))
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''

  return {
    q: params.get('q') ?? '',
    selection: params.get('sel') ?? '',
    side,
    status,
    from: ISO_DAY.test(from) ? from : '',
    to: ISO_DAY.test(to) ? to : '',
    rows,
    page,
  }
}

/** True when anything narrows the log. Page and row cap are navigation, not
 *  filters, so they do not light up Clear or change the count's wording. */
export function betFiltersActive(filters: BetFilters): boolean {
  return (
    filters.q.trim().length > 0 ||
    filters.selection.length > 0 ||
    filters.side !== 'all' ||
    filters.status !== 'open' ||
    filters.from.length > 0 ||
    filters.to.length > 0
  )
}

/**
 * An open-ended from/to still has to become a `DateRange`, whose fields are both
 * required. `inRange` compares isoDay strings lexicographically, so the sentinels
 * are the widest representable days rather than a special case in the API.
 */
function rangeOf(filters: BetFilters): DateRange | undefined {
  if (!filters.from && !filters.to) return undefined
  return { from: filters.from || '0000-01-01', to: filters.to || '9999-12-31' }
}

export function betQueryOf(marketId: string, filters: BetFilters): BetQuery {
  return {
    marketId,
    statuses: STATUS_QUERY[filters.status],
    sides: filters.side === 'all' ? undefined : [filters.side],
    selections: filters.selection ? [filters.selection] : undefined,
    search: filters.q.trim() || undefined,
    range: rangeOf(filters),
    page: filters.page,
    pageSize: filters.rows,
  }
}
